import React from "react";
import {Alert, Button, Table} from "react-bootstrap";
import {useMutation, useQuery} from "react-query";
import {getPaymentCardFormHtml} from "Api/Query/PaymentCardQuery";
import {getPaymentMethods, PaymentMethod} from "Api/Query/PaymentMethodQuery";
import {deletePaymentCard, setDefaultPaymentCard} from "Api/Mutation/PaymentCardMutation";
import {Notificator} from "Services/Notificator/Notificator";
import {ErrorMessageBuilder} from "Services/Notificator/ErrorMessageBuilder";

interface Props {
    tutorId: string,
}

export const CreditCardManager: React.FC<Props> = ({tutorId}) => {

    const paymentMethodsQuery = useQuery({
        queryKey: ['paymentMethods'],
        queryFn: () => getPaymentMethods(),
    })

    const paymentMethods: PaymentMethod[] = paymentMethodsQuery.data?.data?._result?.items ?? []

    const deleteMutation = useMutation(
        (cardId: string) => deletePaymentCard(cardId),
        {
            onSuccess: () => {
                paymentMethodsQuery.refetch()
                Notificator.success('Tarjeta eliminada correctamente')
            },
            onError: (error: any) => Notificator.error(ErrorMessageBuilder.create(error), 'Error'),
        }
    )

    const setDefaultMutation = useMutation(
        (cardId: string) => setDefaultPaymentCard(cardId),
        {
            onSuccess: () => {
                paymentMethodsQuery.refetch()
                Notificator.success('Tarjeta predeterminada actualizada')
            },
            onError: (error: any) => Notificator.error(ErrorMessageBuilder.create(error), 'Error'),
        }
    )

    const handleAddCard = async () => {
        // window.open must be called synchronously within the click handler.
        // Calling it after an await causes browsers to treat it as an unsolicited
        // popup and block it. We open it first, then fill it once the data arrives.
        const w = 1000, h = 800
        const left = Math.round((window.screen.width - w) / 2)
        const top = Math.round((window.screen.height - h) / 2)
        const popup = window.open('about:blank', '_blank', `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`)

        try {
            const response = await getPaymentCardFormHtml()
            const formHtml: string = response?.data?._result?.formHtml ?? ''

            if (!formHtml || !popup) return

            popup.document.open()
            popup.document.write(`<!DOCTYPE html><html><body>${formHtml}<script>document.getElementById('redsys_form').submit();<\/script></body></html>`)
            popup.document.close()

            const interval = setInterval(() => {
                if (popup.closed) {
                    clearInterval(interval)
                    paymentMethodsQuery.refetch()
                }
            }, 2500)
        } catch (error: any) {
            popup?.close()
            Notificator.error(ErrorMessageBuilder.create(error), 'Error')
        }
    }

    return (
        <>
            <h4 className={"purple mt-4 mb-3"}>Métodos de Pago — Tarjeta de Crédito</h4>

            {
                !paymentMethodsQuery.isLoading && paymentMethods.length === 0 &&
                <Alert variant={"warning"}>
                    Es necesario tener un método de pago de tarjeta de crédito registrado para poder inscribirse en las
                    actividades cuyo método de pago sea tarjeta de crédito.
                </Alert>
            }

            <Button
                className={"purple-btn"}
                onClick={handleAddCard}
            >
                Añadir tarjeta
            </Button>

            {
                paymentMethods.length > 0 &&
                <Table responsive bordered hover className={"mt-3"}>
                    <thead>
                    <tr>
                        <th>Marca</th>
                        <th>Caducidad</th>
                        <th>Estado</th>
                        <th>Por defecto</th>
                        <th>Fecha de alta</th>
                        <th>Acciones</th>
                    </tr>
                    </thead>
                    <tbody>
                    {
                        paymentMethods.map((method) => (
                            <tr key={method.id}>
                                <td>{method.cardBrand.toUpperCase()} {method.cardLast4 || ''}</td>
                                <td>{method.cardExpiry}</td>
                                <td>{method.status}</td>
                                <td>{method.isDefault ? 'Sí' : 'No'}</td>
                                <td>{method.createdAt}</td>
                                <td>
                                    {!method.isDefault &&
                                        <Button
                                            variant={"outline-secondary"}
                                            size={"sm"}
                                            className={"me-2"}
                                            disabled={setDefaultMutation.isLoading}
                                            onClick={() => setDefaultMutation.mutate(method.id)}
                                        >
                                            Hacer predeterminada
                                        </Button>
                                    }
                                    <Button
                                        variant={"outline-danger"}
                                        size={"sm"}
                                        disabled={deleteMutation.isLoading}
                                        onClick={() => {
                                            if (window.confirm('¿Estás seguro de que deseas eliminar esta tarjeta?')) {
                                                deleteMutation.mutate(method.id)
                                            }
                                        }}
                                    >
                                        Eliminar
                                    </Button>
                                </td>
                            </tr>
                        ))
                    }
                    </tbody>
                </Table>
            }
        </>
    )
}
